/* eslint-disable no-restricted-syntax */
import { ChevronDownIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { RunDetailView } from '@/components/voice-agent/drawers/RunHistory';
import type { BlandCall } from '@/services/api/blandCallService';
import { useGetTaskRunsQuery } from '@/services/api/blandService';
import { BPOAction, VoiceAIAgentRunSchema } from '@/types/enhanced-bland-call';

type PhoneRow = {
  label: string;
  phone: string;
  source: 'carrier' | 'adjuster_coverage' | 'adjuster_manual';
};

const countBpoCalls = (actions?: BPOAction[] | null): number => {
  if (!Array.isArray(actions) || actions.length === 0) return 0;
  const ids = actions.reduce<Set<string>>((acc, action, index) => {
    const metadata =
      (action.metadata && typeof action.metadata === 'object'
        ? (action.metadata as Record<string, unknown>)
        : {}) || {};
    const callId =
      action.ringcentral_call_id ||
      action.dialpad_call_id ||
      (metadata.ringcentral_call_id as string | undefined) ||
      (metadata.dialpad_call_id as string | undefined) ||
      (metadata.call_id as string | undefined) ||
      `bpo-${index}`;
    acc.add(callId);
    return acc;
  }, new Set<string>());
  return ids.size;
};

interface ClaimCallDetailsProps {
  matterId?: string;
  taskIdForRuns?: string;
  latestBland: BlandCall | null;
  phoneRows: PhoneRow[];
  onManualCall: (phone?: string, label?: string) => void;
  /** Optional: send this task directly to BPO (no calls). */
  onSendToBpo?: () => void;
}

export const ClaimCallDetails: React.FC<ClaimCallDetailsProps> = ({
  matterId,
  taskIdForRuns,
  latestBland,
  phoneRows,
  onManualCall,
  onSendToBpo,
}) => {
  const { data: runsData } = useGetTaskRunsQuery(
    taskIdForRuns && matterId
      ? { matterId, taskId: taskIdForRuns }
      : (skipToken as typeof skipToken),
  );

  const runs: VoiceAIAgentRunSchema[] = useMemo(() => {
    const list = runsData?.runs || [];
    return list
      .slice()
      .sort(
        (a, b) =>
          new Date(b.created_at || '').getTime() -
          new Date(a.created_at || '').getTime(),
      );
  }, [runsData]);

  const [expandedRunIds, setExpandedRunIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!runs.length) return;
    setExpandedRunIds((prev) => {
      if (prev.size > 0) return prev;
      const next = new Set(prev);
      next.add(runs[0].id);
      return next;
    });
  }, [runs]);

  const toggleRun = (runId: string) => {
    setExpandedRunIds((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  };

  const formatTimestamp = (value?: string | null): string => {
    if (!value) return 'Unknown';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? 'Unknown'
      : date.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
  };

  const outcomeFields = useMemo(() => {
    const meta = latestBland?.finch_metadata || {};
    const entries: { label: string; value: string | null }[] = [
      { label: 'claim number', value: meta.claim_number || null },
      { label: 'policy number', value: meta.policy_number || null },
      { label: 'adjuster name', value: meta.adjuster_name || null },
      { label: 'adjuster phone', value: meta.adjuster_phone || null },
      { label: 'adjuster fax', value: meta.adjuster_fax || null },
      {
        label: 'LOR destination',
        value: meta.lor_destination || (meta.adjuster_fax ? 'fax' : null),
      },
    ];
    return entries;
  }, [latestBland]);

  return (
    <div className="space-y-3">
      {onSendToBpo && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={onSendToBpo}
            aria-label="Send to BPO"
          >
            <UserGroupIcon className="h-4 w-4 mr-2" />
            Send to BPO
          </Button>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">
          Voice AI runs
        </div>
        {runs.length === 0 ? (
          <div className="rounded border border-dashed border-gray-200 p-4 text-sm text-gray-500">
            No Voice AI runs yet.
          </div>
        ) : (
          runs.map((run) => {
            const isExpanded = expandedRunIds.has(run.id);
            const voiceCallCount = run.call_results?.length ?? 0;
            const bpoCallCount = countBpoCalls(run.bpo_actions);
            const statusLabel = run.status
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (char) => char.toUpperCase());

            return (
              <div key={run.id} className="rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => toggleRun(run.id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
                  aria-expanded={isExpanded}
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {statusLabel}
                    </p>
                    <p className="text-xs text-gray-500">
                      Started {formatTimestamp(run.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <span>
                        {voiceCallCount} AI call
                        {voiceCallCount === 1 ? '' : 's'}
                      </span>
                      <span className="text-gray-300">•</span>
                      <span>
                        {bpoCallCount} BPO call
                        {bpoCallCount === 1 ? '' : 's'}
                      </span>
                    </div>
                    <ChevronDownIcon
                      className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4">
                    <RunDetailView run={run} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {latestBland && (
        <div className="rounded border p-2">
          <div className="text-xs text-gray-600 mb-1">
            Information Collected
          </div>
          <ul className="text-sm text-gray-800 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {outcomeFields.map((f) => (
              <li key={f.label} className="flex gap-2">
                <span className="font-medium min-w-[140px] capitalize">
                  {f.label}:
                </span>
                <span className={f.value ? '' : 'italic text-gray-500'}>
                  {f.value || 'Not captured'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {phoneRows.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-gray-600">Contact Options</div>
          <div className="rounded border divide-y">
            {phoneRows.map((row, idx) => (
              <div
                key={`${row.label}-${idx}`}
                className="flex items-center justify-between gap-3 p-2"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-50 text-gray-700">
                    Carrier
                  </span>
                  <span className="text-sm text-gray-800 truncate">
                    {row.label}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono text-gray-900">
                    {row.phone}
                  </span>
                  <Button
                    size="sm"
                    aria-label={`Call ${row.label} at ${row.phone}`}
                    onClick={() => onManualCall(row.phone, row.label)}
                  >
                    Call
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
